import React, { useState } from 'react';
import useSWR from 'swr';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';

const STATUS = {
  blocked: { label: 'Blocked', color: 'error' },
  reopen_requested: { label: 'Reopening requested', color: 'warning' },
  lifted: { label: 'Reopened', color: 'success' },
  upheld: { label: 'Kept blocked', color: 'default' },
};

/** Accounts blocked for security (e.g. three wrong merge codes): reopen them or keep them blocked. */
export default function BlockedAccountsTab() {
  const [showAll, setShowAll] = useState(false);
  const { data: rows, mutate, error } = useSWR(`/admin/account-blocks/${showAll ? '?status=all' : ''}`, fetcher);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);

  const decide = async (row, decision) => {
    setBusy(row.id);
    try {
      await axiosService.post(`/admin/account-blocks/${row.id}/decision/`, { decision, note: notes[row.id] || '' });
      setToast({ severity: 'success', text: decision === 'lift' ? `${row.user.name} can use Otable again.` : `${row.user.name} stays blocked.` });
      mutate();
    } catch (err) {
      setToast({ severity: 'error', text: apiErrorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Accounts blocked after three wrong codes while merging accounts. The person can ask to reopen theirs.
        </Typography>
        <FormControlLabel control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />} label="Show reviewed" />
      </Box>
      {toast && <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ mb: 2 }}>{toast.text}</Alert>}
      {error && <Alert severity="error">Could not load blocked accounts.</Alert>}
      {!rows ? <CircularProgress size={24} /> : rows.length === 0 ? (
        <Typography color="text.secondary">No blocked accounts.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((row) => {
            const st = STATUS[row.status] || { label: row.status, color: 'default' };
            const open = row.status === 'blocked' || row.status === 'reopen_requested';
            return (
              <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography sx={{ fontWeight: 600 }}>{row.user.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{row.user.email}</Typography>
                  <Chip size="small" label={st.label} color={st.color} />
                  <Box flex={1} />
                  <Typography variant="caption" color="text.secondary">{new Date(row.created).toLocaleString()}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>{row.reason}</Typography>
                {row.details?.other_email && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Tried to merge with {row.details.other_email} · {row.details.wrong_codes} wrong codes
                  </Typography>
                )}
                {row.reopen_message && (
                  <Box sx={{ mt: 1, p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Request to reopen{row.reopen_requested_at ? `, ${new Date(row.reopen_requested_at).toLocaleString()}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{row.reopen_message}</Typography>
                  </Box>
                )}
                {open ? (
                  <Box display="flex" gap={1} alignItems="center" mt={1.5} flexWrap="wrap">
                    <TextField size="small" placeholder="Note (optional)" value={notes[row.id] || ''} sx={{ flex: 1, minWidth: 200 }}
                      onChange={(e) => setNotes({ ...notes, [row.id]: e.target.value })} />
                    <Button variant="contained" color="success" disabled={busy === row.id} onClick={() => decide(row, 'lift')}>Reopen account</Button>
                    <Button variant="outlined" color="error" disabled={busy === row.id} onClick={() => decide(row, 'uphold')}>Keep blocked</Button>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {st.label} by {row.reviewed_by || 'an administrator'}{row.review_note ? `: ${row.review_note}` : ''}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
